import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { useCurrency } from "../contexts/CurrencyContext";
import {
  BadgeCent,
  BadgeIndianRupee,
  Coins,
  Gem,
  GemIcon,
  Globe,
  LogIn,
  LogOut,
} from "lucide-react";
import { useAuth0 } from "@auth0/auth0-react";
import { Button } from "./ui/button";
import { useWallet } from "../contexts/WalletContext";
import { useUser } from "@/contexts/userContext";

const CurrencySelector = () => {
  const { currency, setCurrency, getAvailableCurrencies, getCurrencySymbol } =
    useCurrency();
  const navigate = useNavigate();
  const { walletBalance, setWalletBalance } = useWallet();
  const currencies = getAvailableCurrencies();
  const [hasRegistered, setHasRegistered] = useState(false);
  const {
    user,
    logout,
    loginWithRedirect,
    isLoading,
    loginWithPopup,
    isAuthenticated,
  } = useAuth0();
 
  // console.log("Auth0 User Data in Currency Selector:", user);
const [imageError, setImageError] = useState(false);
  const [profilePic, setProfilePic] = useState("");
  const [userData, setUserData] = useState({});
  const [currentUser, setCurrentUser] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [extraFields, setExtraFields] = useState({
    mobile: "",
    companyName: "",
  });
  const menuRef = useRef(null);
  // console.log("User Register Details", user);

  const handleSubscribe = () => {
    // navigate("/Pricing");
     navigate("/Pricing", {
    state: { redirectTo: "/calculator" }, // Home
  });
    setMenuOpen(false);
  };

  const { setUserState } = useUser();
  const { userState } = useUser();
  // console.log("UserState Data in Currency", userState);

  const credit = userState.profile?.credit;
  const plan = userState?.profile?.subscriptionPlan;
  const isSubscribed = userState.profile?.isSubscribed;
  const isHighestPlan = plan === "Business Pack";

  // New Logic
  useEffect(() => {
    const registerUser = async () => {
      if (!isAuthenticated || !user || hasRegistered) return;

      try {
        /* ----------------------------------
         1️⃣ Check if user exists
      ---------------------------------- */
        const userRes = await fetch(
          `${process.env.REACT_APP_BASE_URL}/api/v1/user/getuser/${encodeURIComponent(
            user.sub,
          )}`,
        );

        let userProfile = null;

        if (userRes.ok) {
          const userData = await userRes.json();
          userProfile = userData.user;
        } else {
          /* ----------------------------------
           2️⃣ Register new user
        ---------------------------------- */
          await fetch(
            `${process.env.REACT_APP_BASE_URL}/api/v1/user/register-user`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                user_id: user.sub,
                email: user.email,
                email_verified: user.email_verified,
                name: user.name,
                profile_pic: user.picture,
              }),
            },
          );

          // Fetch newly created user
          const newUserRes = await fetch(
            `${process.env.REACT_APP_BASE_URL}/api/v1/user/getuser/${encodeURIComponent(
              user.sub,
            )}`,
          );

          const newUserData = await newUserRes.json();
          userProfile = newUserData.user;
          setShowProfileModal(true);
        }

        /* ----------------------------------
         3️⃣ Fetch ACCESS & PERMISSIONS
      ---------------------------------- */
        const accessRes = await fetch(
          `${process.env.REACT_APP_BASE_URL}/api/v1/user/get-user-access/${encodeURIComponent(
            user.sub,
          )}`,
        );

        if (!accessRes.ok) {
          throw new Error("Failed to fetch user access");
        }

        const accessData = await accessRes.json();

        /* ----------------------------------
         4️⃣ SET GLOBAL USER STATE (FINAL)
      ---------------------------------- */
        setUserState({
          loading: false,
          profile: {
            ...userProfile,
            credit: accessData.credit,
            subscriptionPlan: accessData.subscriptionPlan,
          },
          permissions: accessData.permissions,
        });

        setHasRegistered(true);

        /* ----------------------------------
         5️⃣ Profile completeness check
      ---------------------------------- */
        if (!userProfile.mobile || !userProfile.companyName) {
          setShowProfileModal(true);
        }
      } catch (error) {
        console.error("❌ User initialization failed:", error);
      }
    };

    registerUser();
  }, [isAuthenticated, user, hasRegistered]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // 🔹 Handle input change for profile modal
  const handleChange = (e) => {
    const { name, value } = e.target;
    setExtraFields((prev) => ({ ...prev, [name]: value }));
  };

  // 🔹 Save profile data to backend
  const handleSaveProfile = async () => {
    if (!extraFields.mobile || !extraFields.companyName) {
      alert("Please fill in all fields");
      return;
    }

    try {
      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/v1/user/update-profile`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user_id: user.sub,
            mobile: extraFields.mobile,
            companyName: extraFields.companyName,
          }),
        },
      );
      const data = await res.json();
      // console.log("✅ Profile updated:", data);
      setShowProfileModal(false);
      setCurrentUser(data?.user || {});
    } catch (err) {
      console.error("❌ Error saving profile:", err);
      alert("Failed to save profile.");
    }
  };

  // console.log("CURRENT USER DATA FETCH", userData);

  return (
    <div className="flex items-center gap-2 relative" ref={menuRef}>
      {/* <div className="flex items-center gap-2">
        <Globe className="h-4 w-4 text-slate-500" />
      </div>

      {/* Currency Selector */}
      {/* <Select value={currency} onValueChange={setCurrency}>
        <SelectTrigger className="w-36 h-8 text-sm">
          <SelectValue placeholder="Select currency">
            <div className="flex items-center gap-2">
              <span className="font-semibold">{getCurrencySymbol()}</span>
              <span>{currency}</span>
            </div>
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {currencies.map((curr) => (
            <SelectItem key={curr.code} value={curr.code}>
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-3">
                  <span className="font-semibold w-6">{curr.symbol}</span>
                  <span className="font-medium">{curr.code}</span>
                  <span className="text-slate-500 text-sm">{curr.name}</span>
                </div>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>  */}

      {/* Authenticated User Section */}
      {isAuthenticated && user ? (
        <div className="flex items-center">
             {userState.loading ? (
                // 🔹 Skeleton Loader
                <span
                  className="
      inline-flex items-center
      px-4 py-1
      rounded-full
      bg-gray-200
      animate-pulse
    "
                >
                  <span className="w-4 h-4 mr-2 rounded-full bg-gray-300" />
                  <span className="w-8 h-3 rounded bg-gray-300" />
                </span>
              ) : (
                // 🔹 Actual Credit Badge
                // <span className="bg-yellow-100 font-bold text-yellow-800 text-sm inline-flex items-center px-1.5 py-0.5 rounded-full dark:bg-yellow-900 dark:text-yellow-300">
                  <span className="hidden md:inline-flex bg-yellow-100 font-bold text-yellow-800 text-sm items-center px-1.5 py-0.5 rounded-full">
                  <BadgeCent size={16} className="mr-2 text-yellow-800" />
                  {credit}
                </span>
              )}
          <div className="relative">
         
            <div
  className="flex items-center space-x-2 cursor-pointer"
  onMouseEnter={() => setMenuOpen(true)}
  onMouseLeave={() => setMenuOpen(false)}
>

           
{/* Working code  */}
              {/* {user?.picture ? (
                <img
                
                  src={user.picture}
                  alt={user.name || "User"}
                  className="w-10 h-10 mx-2 rounded-full object-cover border-2 border-blue-500"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <span className="w-10 h-10 rounded-full bg-blue-500 text-white flex items-center justify-center font-bold text-lg">
                  {user.name?.charAt(0)?.toUpperCase() || "U"}
                </span>
              )} */}

              {user?.picture && !imageError ? (
  <img
    src={user.picture}
    alt={user.email || "User"}
    className="w-10 h-10 mx-2 rounded-full object-cover border-2 border-blue-500"
    referrerPolicy="no-referrer"
    onError={() => setImageError(true)}
  />
) : (
  <span className="w-10 h-10 rounded-full bg-blue-500 text-white flex items-center justify-center font-bold text-lg mx-2">
    {user?.email?.charAt(0)?.toUpperCase() || "U"}
  </span>
)}
            </div>

            {/* Dropdown Menu */}
            {/* {menuOpen && (
              <div className="absolute -right-9 mt-3 w-56 bg-white border border-gray-200 rounded-xl shadow-lg p-3 z-50">
                <div className="px-3 py-2 border-b border-gray-100">
                  <p className="text-sm font-semibold text-gray-800 truncate">
                    {user.name}
                  </p>
                  <p className="text-xs text-gray-500">{user.email}</p>
                </div>

              </div>
            )} */}
            {menuOpen && (
  <div className="absolute right-0 mt-3 w-64 bg-white border border-gray-200 rounded-xl shadow-lg p-3 z-50">

    {/* Profile Info */}
    <div className="px-3 py-2 border-b border-gray-100">
      <p className="text-sm font-semibold text-gray-800 truncate">
        {user.name}
      </p>
      <p className="text-xs text-gray-500">{user.email}</p>
    </div>

    {/* MOBILE ONLY PLAN INFO */}
<div className="md:hidden mt-3">

  <div className="flex items-center justify-between px-3 py-2 rounded-lg border border-green-200 bg-green-50">

    <div>
      <p className="text-[10px] font-semibold uppercase text-gray-500">
        Current Plan
      </p>
      <p className="text-sm font-bold text-gray-900">
        {plan}
      </p>
    </div>

    <button
      onClick={() => {
        handleSubscribe();
        setMenuOpen(false);
      }}
      disabled={isHighestPlan}
      className={`px-3 py-1 text-xs font-semibold rounded-md ${
        isHighestPlan
          ? "bg-green-300 text-white cursor-not-allowed"
          : "bg-green-600 text-white hover:bg-green-700"
      }`}
    >
      {isHighestPlan ? "Subscribed" : "Upgrade"}
    </button>

  </div>

</div>

    {/* Mobile Only Section */}
    <div className="md:hidden mt-3 space-y-2">

      {/* Credit */}
      <div className="flex items-center justify-between px-3 py-2 bg-yellow-50 rounded-lg">
        <span className="text-sm font-medium">Credits</span>
        <span className="text-sm font-bold text-yellow-700">{credit}</span>
      </div>

      {/* Saved Projects */}
      <button
        onClick={() => {
          navigate("/admin/table");
          setMenuOpen(false);
        }}
        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 rounded-lg"
      >
        My Saved Projects
      </button>

      {/* Logout */}
      <button
        onClick={() =>
          logout({
            logoutParams: {
              returnTo: window.location.origin,
            },
          })
        }
        className="flex items-center w-full px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg"
      >
        <LogOut size={16} className="mr-2" />
        Logout
      </button>
    </div>

  </div>
)}
          </div>
        </div>
      ) : (
        <Button
          onClick={() => loginWithRedirect()}
          style={{ background: "#1ac47d" }}
          className="text-white flex items-center"
        >
          <LogIn size={18} className="mr-1" /> Login
        </Button>
        //
      )}

      <div>
        {isAuthenticated && user && (
<div className="hidden md:flex  flex items-center">
  <div
  className=" w-[185px]
    flex items-center justify-between
    px-4 py-3 mt-2
    rounded-xl
    border border-green-200
    bg-gradient-to-r from-green-50 to-green-100
    shadow-sm
  "
>
  {/* Plan Info */}
  <div className=" flex flex-col gap-0.5 mr-2">
    <span className="text-[8px] font-semibold tracking-wider uppercase">
      Current Plan
    </span>

    <div className="flex items-center gap-2">
      <span className="text-sm font-bold text-gray-900">
        {plan}
      </span>

    </div>
  </div>

  {/* Upgrade button */}
 <button
  onClick={handleSubscribe}
  disabled={isHighestPlan}
  className={`
    px-4 py-2 text-xs font-semibold rounded-lg transition-all
    ${
      isHighestPlan
        ? "bg-green-300 text-white cursor-not-allowed"
        : "bg-green-600 text-white hover:bg-green-700 active:bg-green-800 shadow-md"
    }
  `}
>
  {isHighestPlan ? "Subscribed" : "Upgrade"}
</button>


</div>

            <button
  onClick={() =>
    logout({
      logoutParams: {
        returnTo: window.location.origin, // 👈 redirects to "/"
      },
    })
  }
  // className="flex items-center w-full px-3 py-2 mt-1 text-red-600 hover:bg-red-50 rounded-lg"
  className="hidden md:flex items-center w-full px-3 py-2 mt-1 text-red-600 hover:bg-red-50 rounded-lg"
>
  <LogOut size={16} className="mr-2" />
  Logout
</button>

</div>

        )}
      </div>
    </div>
  );
};

export default CurrencySelector;
